import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchProducts } from '../../api/product';
import { useCompanyStore } from '../../store/companyStore';
import { formatQty, formatWon, Product } from '../../types/product';
import {
  clearProductThumbCache,
  invalidateProductThumb,
  pruneProductThumbCache,
} from '../../utils/productThumbCache';
import ProductCardThumb from './ProductCardThumb';
import ProductDetail from './ProductDetail';
import ProductForm from './ProductForm';
import ProductTagChips from './ProductTagChips';

import '../contract/ContractPanel.css';
import './ProductForm.css';

import { reportError } from '../../utils/notify';
type PanelView =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'detail'; productId: number }
  | { type: 'edit'; productId: number };

const LIST_VIEW: PanelView = { type: 'list' };

function ProductPanel() {
  const companyId = useCompanyStore((state) => state.companyId);
  const [view, setView] = useState<PanelView>(LIST_VIEW);
  const [products, setProducts] = useState<Product[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProducts = useCallback(async (keyword?: string) => {
    try {
      setLoading(true);
      const list = await fetchProducts(keyword);
      setProducts(list);
      pruneProductThumbCache(list.map((item) => item.productId));
      setError('');
    } catch {
      reportError(setError, '제품 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setView(LIST_VIEW);
    setSelectedId(null);
    setSearchKeyword('');
    clearProductThumbCache();
    void loadProducts();
  }, [companyId, loadProducts, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => {
      void loadProducts(searchKeyword);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchKeyword, loadProducts, view.type]);

  const selectedProduct = useMemo(
    () => products.find((item) => item.productId === selectedId) ?? null,
    [products, selectedId]
  );

  const goList = () => {
    setView(LIST_VIEW);
    void loadProducts(searchKeyword);
  };

  return (
    <main className="contract-panel product-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>제품목록</strong>
          <span>전체 목록</span>
        </div>

        <div className="contract-panel__search">
          <input
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="제품코드 / 제품명 / 해시태그 검색"
          />
        </div>

        <div className="contract-panel__list-wrap">
          {loading ? (
            <p className="contract-panel__hint">불러오는 중...</p>
          ) : products.length === 0 ? (
            <p className="contract-panel__hint">표시할 제품이 없습니다.</p>
          ) : (
            <ul className="contract-panel__list product-side-list">
              <li>
                <button
                  type="button"
                  className={`contract-panel__list-item product-side-item${
                    view.type === 'list' && selectedId == null ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    setSelectedId(null);
                    setView(LIST_VIEW);
                  }}
                >
                  <span className="contract-panel__list-name">전체 보기</span>
                  <span className="contract-panel__list-meta">{products.length}건</span>
                </button>
              </li>
              {products.map((product) => {
                const stockLow =
                  (product.safetyStock ?? 0) > 0 &&
                  (product.stockQty ?? 0) <= (product.safetyStock ?? 0);
                return (
                  <li key={product.productId}>
                    <button
                      type="button"
                      className={[
                        'contract-panel__list-item',
                        'product-side-item',
                        selectedId === product.productId ? 'is-active' : '',
                        product.useYn === 'N' ? 'is-unused' : '',
                        stockLow ? 'is-low' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        setSelectedId(product.productId);
                        setView(LIST_VIEW);
                      }}
                      onDoubleClick={() => {
                        setSelectedId(product.productId);
                        setView({ type: 'detail', productId: product.productId });
                      }}
                    >
                      <span className="product-side-item__top">
                        <span className="contract-panel__list-name">{product.productName}</span>
                        <span
                          className={`product-side-item__status${
                            product.useYn === 'N' ? ' is-off' : ''
                          }`}
                        >
                          {product.useYn === 'N' ? '미사용' : '사용'}
                        </span>
                      </span>
                      {(product.tags?.length ?? 0) > 0 && (
                        <span className="product-panel__side-tags">
                          <ProductTagChips tags={product.tags ?? []} compact />
                        </span>
                      )}
                      <span className="contract-panel__list-meta">
                        {[
                          product.productCode || null,
                          product.category || null,
                          formatWon(product.supplyPrice),
                          `재고 ${formatQty(product.stockQty)}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      <section className="contract-panel__main">
        {view.type === 'create' ? (
          <ProductForm
            mode="create"
            onCancel={goList}
            onSaved={(saved) => {
              invalidateProductThumb(saved.productId);
              setSelectedId(saved.productId);
              setView({ type: 'detail', productId: saved.productId });
              void loadProducts(searchKeyword);
            }}
          />
        ) : view.type === 'edit' ? (
          <ProductForm
            mode="edit"
            productId={view.productId}
            onCancel={() => setView({ type: 'detail', productId: view.productId })}
            onSaved={(saved) => {
              invalidateProductThumb(saved.productId);
              setSelectedId(saved.productId);
              setView({ type: 'detail', productId: saved.productId });
              void loadProducts(searchKeyword);
            }}
          />
        ) : view.type === 'detail' ? (
          <ProductDetail
            productId={view.productId}
            onBack={goList}
            onEdit={() => setView({ type: 'edit', productId: view.productId })}
            onDeleted={() => {
              invalidateProductThumb(view.productId);
              goList();
            }}
          />
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>제품 현황</h2>
                <p>
                  {selectedProduct
                    ? `선택: ${selectedProduct.productName} (더블클릭 시 상세)`
                    : `표시 ${products.length}건 · 행 더블클릭으로 상세`}
                </p>
              </div>
              <button
                type="button"
                className="contract-panel__primary"
                onClick={() => setView({ type: 'create' })}
              >
                제품 등록
              </button>
            </div>

            {error && <p className="contract-panel__error">{error}</p>}

            <div className="contract-panel__content">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : products.length === 0 ? (
                <p className="contract-panel__hint">제품을 등록해 주세요.</p>
              ) : (
                <div className="product-card-list">
                  {products.map((product, index) => {
                    const stockLow =
                      (product.safetyStock ?? 0) > 0 &&
                      (product.stockQty ?? 0) <= (product.safetyStock ?? 0);
                    const unused = product.useYn === 'N';
                    return (
                      <article
                        key={product.productId}
                        className={[
                          'product-card',
                          selectedId === product.productId ? 'is-selected' : '',
                          unused ? 'is-unused' : '',
                          stockLow ? 'is-low' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => setSelectedId(product.productId)}
                        onDoubleClick={() =>
                          setView({ type: 'detail', productId: product.productId })
                        }
                      >
                        <ProductCardThumb
                          className="product-card__media"
                          productId={product.productId}
                          hasImage={product.hasImage}
                          updatedAt={product.updatedAt}
                        >
                          <span className="product-card__order">
                            {(product.sortOrder ?? index) + 1}
                          </span>
                        </ProductCardThumb>
                        <div className="product-card__body">
                          <div className="product-card__head">
                            <div>
                              <p className="product-card__code">
                                {product.productCode || '코드 없음'}
                              </p>
                              <h3 className="product-card__title">{product.productName}</h3>
                            </div>
                            <div className="product-card__badges">
                              <span className="product-card__badge">
                                {product.category || '미분류'}
                              </span>
                              <span
                                className={`product-card__badge${unused ? ' is-off' : ' is-on'}`}
                              >
                                {unused ? '미사용' : '사용'}
                              </span>
                              {stockLow ? (
                                <span className="product-card__badge is-low">재고부족</span>
                              ) : null}
                            </div>
                          </div>
                          {(product.tags?.length ?? 0) > 0 ? (
                            <div className="product-card__tags">
                              <ProductTagChips tags={product.tags ?? []} compact />
                            </div>
                          ) : null}
                          <div className="product-card__stats">
                            <div className="product-card__stat">
                              <span>단위</span>
                              <strong>{product.unit || 'EA'}</strong>
                            </div>
                            <div className="product-card__stat">
                              <span>원가</span>
                              <strong>{formatWon(product.costPrice)}</strong>
                            </div>
                            <div className="product-card__stat">
                              <span>공급단가</span>
                              <strong>{formatWon(product.supplyPrice)}</strong>
                            </div>
                            <div className="product-card__stat">
                              <span>재고</span>
                              <strong className={stockLow ? 'is-low' : undefined}>
                                {formatQty(product.stockQty)}
                              </strong>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default ProductPanel;
